import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import {
    MousePointer2,
    Check,
    Bike,
    Plus,
    Package,
    ShoppingBag,
    Minus,
    X,
    Signal,
    Wifi,
    BatteryMedium,
    Lightbulb,
    Menu,
    ArrowLeft,
    ArrowRight,
} from "lucide-react";

const ease = [0.16, 1, 0.3, 1];
const overlayPhases = ["order", "cursor", "accepted", "done", "order2", "rejected"];

const ORDER_ONE = {
    price: "₹700",
    extra: "₹93",
    pickupKm: "0.4 km",
    pickupAddr: "Demo Pickup Point — Green Park, Block A",
    dropKm: "xx km",
    dropAddr: "Demo Drop Point — Lake View Road",
};
const ORDER_TWO = {
    price: "₹32",
    extra: null,
    pickupKm: "6.8 km",
    pickupAddr: "Far Pickup Point — Old Mill Road",
    dropKm: "xx km",
    dropAddr: "Demo Drop Point — Lake View Road",
};

const guides = [
    "Choose your apps",
    "Set pickup distance",
    "Set drop-off preference",
    "Review & start",
];

const screenStagger = {
    hidden: {},
    show: { transition: { staggerChildren: 0.09, delayChildren: 0.08 } },
};
const screenItem = {
    hidden: { opacity: 0, y: 18 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5, ease } },
};
const Item = ({ children, className = "" }) => (
    <motion.div variants={screenItem} className={className}>
        {children}
    </motion.div>
);

const AppHeader = () => (
    <div className="flex items-center justify-between rounded-2xl bg-[#ECECF1] px-3.5 py-2.5">
        <div>
            <p className="font-display text-sm font-bold leading-tight text-[#4B3FBF]">
                Rapid Click
            </p>
            <p className="text-[10px] text-[#55556B]">Smart order automation</p>
        </div>
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-[#4B3FBF]">
            <Menu size={14} />
        </div>
    </div>
);

const Tip = ({ children }) => (
    <div className="flex items-start gap-2 rounded-xl bg-[#E8E9FC] p-2.5">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#5B5BD6]">
            <Lightbulb size={12} className="text-white" />
        </span>
        <div>
            <p className="text-[10px] font-bold text-[#1E1E30]">Smart Assistant</p>
            <p className="text-[10px] leading-snug text-[#55556B]">{children}</p>
        </div>
    </div>
);

const Nav = ({ onNext, onBack }) => (
    <div className="flex items-center justify-between pt-1">
        {onBack ? (
            <button
                data-testid="demo-back-btn"
                onClick={onBack}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E0E0E6] bg-white text-[#55556B]"
            >
                <ArrowLeft size={14} />
            </button>
        ) : (
            <span />
        )}
        {onNext && (
            <button
                data-testid="demo-continue-btn"
                onClick={onNext}
                className="ml-auto flex items-center gap-1.5 rounded-xl bg-[#5B5BD6] px-4 py-2.5 text-[11px] font-bold text-white transition-transform duration-150 active:scale-90"
            >
                Continue <ArrowRight size={12} />
            </button>
        )}
    </div>
);

const ToggleRow = ({ variant, name, desc }) => (
    <div
        className={`mb-2 flex items-center gap-2.5 rounded-xl border-2 p-2.5 ${
            variant === "rapido"
                ? "border-[#F2C94C] bg-[#FFF6DC]"
                : "border-[#C4B8F5] bg-[#F0EDFD]"
        }`}
    >
        <div
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white ${
                variant === "rapido" ? "bg-[#F0A500]" : "bg-[#7C6FF0]"
            }`}
        >
            {variant === "rapido" ? <Bike size={16} /> : <Plus size={16} />}
        </div>
        <div className="flex-1">
            <p className="text-xs font-bold text-[#1E1E30]">{name}</p>
            <p className="text-[9px] text-[#8A8A99]">{desc}</p>
        </div>
        <div className="flex h-5 w-9 items-center justify-end rounded-full bg-[#B9BDC7] px-0.5">
            <div className="h-4 w-4 rounded-full bg-white shadow" />
        </div>
    </div>
);

const Field = ({ label, value, unit, testid }) => (
    <div data-testid={testid} className="mb-2 rounded-xl border border-[#E0E0E6] px-3 py-2">
        <p className="text-[9px] text-[#8A8A99]">{label}</p>
        <div className="flex items-baseline justify-between">
            <span
                className={`font-display text-lg font-bold ${
                    value ? "text-[#1E1E30]" : "text-[#C4C4CE]"
                }`}
            >
                {value || "0.0"}
            </span>
            <span className="text-[10px] font-medium text-[#8A8A99]">{unit}</span>
        </div>
    </div>
);

const Card = ({ step, title, desc, progress, children }) => (
    <div className="flex flex-1 flex-col rounded-[1.3rem] bg-white p-3.5">
        <p className="font-mono text-[9px] font-bold tracking-[0.15em] text-[#5B5BD6]">{step}</p>
        <h3 className="mt-1 font-display text-lg font-bold text-[#1E1E30]">{title}</h3>
        <p className="mb-2 mt-0.5 text-[10px] text-[#8A8A99]">{desc}</p>
        <div className="mb-3 h-1 w-full overflow-hidden rounded-full bg-[#EDEDF2]">
            <motion.div
                className="h-full rounded-full bg-[#5B5BD6]"
                initial={{ width: 0 }}
                animate={{ width: progress }}
                transition={{ duration: 0.8, ease }}
            />
        </div>
        {children}
    </div>
);

const ScreenOne = ({ onNext, pill, onSelectPill }) => (
    <motion.div
        variants={screenStagger}
        initial="hidden"
        animate="show"
        className="flex h-full flex-col gap-2 p-3 pt-9"
        data-testid="demo-screen-1"
    >
        <Item>
            <AppHeader />
        </Item>
        <Item className="flex flex-1 flex-col">
            <Card
                progress="25%"
                step="STEP 1 OF 4"
                title="Choose your apps"
                desc="Select the apps RapidClick should monitor."
            >
                <ToggleRow variant="rapido" name="Rapido" desc="Monitor ride orders" />
                <ToggleRow variant="porter" name="Porter" desc="Monitor delivery orders" />
                <p className="mb-1.5 mt-2 font-mono text-[8px] tracking-[0.15em] text-[#8A8A99]">
                    ORDER TYPE
                </p>
                <div className="flex gap-1.5">
                    {["All", "Ride", "Parcel"].map((p) => (
                        <button
                            key={p}
                            data-testid={`pill-${p.toLowerCase()}`}
                            onClick={() => onSelectPill(p)}
                            className={`rounded-lg border px-3 py-1.5 text-[10px] font-bold transition-all duration-200 active:scale-90 ${
                                pill === p
                                    ? "border-[#5B5BD6] bg-white text-[#5B5BD6]"
                                    : "border-[#E0E0E6] bg-white text-[#55556B]"
                            }`}
                        >
                            {p}
                        </button>
                    ))}
                </div>
            </Card>
        </Item>
        <Item>
            <Tip>Both services active. You will receive the maximum volume of requests.</Tip>
        </Item>
        <Item>
            <Nav onNext={onNext} />
        </Item>
    </motion.div>
);

const ScreenTwo = ({ onNext, onBack, pickup }) => (
    <motion.div
        variants={screenStagger}
        initial="hidden"
        animate="show"
        className="flex h-full flex-col gap-2 p-3 pt-9"
        data-testid="demo-screen-2"
    >
        <Item>
            <AppHeader />
        </Item>
        <Item className="flex flex-1 flex-col">
            <Card
                progress="50%"
                step="STEP 2 OF 4"
                title="Set pickup distance"
                desc="Choose how far you're willing to travel for pickup."
            >
                <Field
                    testid="field-pickup"
                    label="Maximum pickup distance"
                    value={pickup}
                    unit="km"
                />
                <p className="text-[9px] text-[#8A8A99]">
                    Only orders within this pickup distance can qualify.
                </p>
            </Card>
        </Item>
        <Item>
            <Tip>Optimal pickup range. Balances order volume with manageable distances.</Tip>
        </Item>
        <Item>
            <Nav onNext={onNext} onBack={onBack} />
        </Item>
    </motion.div>
);

const ScreenThree = ({ onNext, onBack, minDrop, maxDrop }) => (
    <motion.div
        variants={screenStagger}
        initial="hidden"
        animate="show"
        className="flex h-full flex-col gap-2 p-3 pt-9"
        data-testid="demo-screen-3"
    >
        <Item>
            <AppHeader />
        </Item>
        <Item className="flex flex-1 flex-col">
            <Card
                progress="75%"
                step="STEP 3 OF 4"
                title="Set drop-off preference"
                desc="Choose the drop-offs that should qualify."
            >
                <div className="mb-3 flex gap-2">
                    <span className="flex-1 rounded-xl border-2 border-[#5B5BD6] bg-white py-1.5 text-center text-[10px] font-bold text-[#5B5BD6]">
                        Distance
                    </span>
                    <span className="flex-1 rounded-xl bg-[#F2F2F5] py-1.5 text-center text-[10px] font-bold text-[#8A8A99]">
                        Area
                    </span>
                </div>
                <Field testid="field-min" label="Minimum drop-off distance" value={minDrop} unit="km" />
                <Field testid="field-max" label="Maximum drop-off distance" value={maxDrop} unit="km" />
            </Card>
        </Item>
        <Item>
            <Tip>Focusing on long-distance trips. These usually yield higher payouts per trip.</Tip>
        </Item>
        <Item>
            <Nav onNext={onNext} onBack={onBack} />
        </Item>
    </motion.div>
);

const ScreenFour = ({ onBack, phase, onStart }) => {
    const active = phase !== "setup" && phase !== "starting";
    return (
        <motion.div
            variants={screenStagger}
            initial="hidden"
            animate="show"
            className="flex h-full flex-col gap-2 p-3 pt-9"
            data-testid="demo-screen-4"
        >
            <Item>
                <AppHeader />
            </Item>
            <Item className="flex flex-1 flex-col">
                <Card
                    progress="100%"
                    step="STEP 4 OF 4"
                    title={active ? "Automation live" : "Review & Start"}
                    desc={
                        active
                            ? "RapidClick is watching for orders."
                            : "Review your setup before starting."
                    }
                >
                <div className="mb-3 flex items-center gap-2.5 rounded-xl bg-[#5B5BD6] p-3 text-white">
                    <span className="relative flex h-2.5 w-2.5 shrink-0">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
                    </span>
                    <div>
                        <p className="text-[11px] font-bold">
                            {active ? "Listening for matching orders…" : "Ready to Start"}
                        </p>
                        <p className="text-[9px] text-white/70">
                            {active
                                ? "New orders will appear at the top."
                                : "All preferences configured. Tap Start to begin."}
                        </p>
                    </div>
                </div>
                <div className="rounded-xl border border-[#E8E8EE] px-3 text-[11px]">
                    {(active
                        ? [
                              ["Apps", "Rapido + Porter"],
                              ["Order type", "Parcel orders"],
                              ["Pickup", "Up to 2 km"],
                              ["Drop-off", "30–100 km"],
                          ]
                        : [
                              ["Apps", "Rapido + Porter"],
                              ["Order type", "Parcel orders"],
                              ["Pickup", "Up to 2 km"],
                              ["Drop-off", "30–100 km"],
                          ]
                    ).map(([k, v]) => (
                        <div
                            key={k}
                            className="flex justify-between border-b border-[#E8E8EE] py-2 last:border-0"
                        >
                            <span className="text-[#8A8A99]">{k}</span>
                            <span className="font-bold text-[#1E1E30]">{v}</span>
                        </div>
                    ))}
                </div>
                <button
                    data-testid="start-automation-btn"
                    onClick={onStart}
                    className={`relative mt-3 w-full rounded-xl py-2.5 text-xs font-bold text-white transition-all duration-500 active:scale-[0.96] ${
                        active && phase !== "starting" ? "bg-[#EF4444]" : "bg-[#5B5BD6]"
                    }`}
                >
                    {phase === "setup" && (
                        <span className="absolute inset-0 animate-ping rounded-xl bg-[#5B5BD6]/40" />
                    )}
                    <span className="relative">
                        {phase === "starting"
                            ? "Starting…"
                            : active
                              ? "Pause Automation"
                              : "Start Automation"}
                    </span>
                </button>
                </Card>
            </Item>
            <Item>
                <Nav onBack={onBack} />
            </Item>
        </motion.div>
    );
};

export default function PhoneDemo() {
    const ref = useRef(null);
    const screenRef = useRef(null);
    const inView = useInView(ref, { margin: "-10% 0px" });
    const [tabVisible, setTabVisible] = useState(true);
    const [screen, setScreen] = useState(0);
    const [phase, setPhase] = useState("setup");
    const [pill, setPill] = useState("All");
    const [pickup, setPickup] = useState("");
    const [minDrop, setMinDrop] = useState("");
    const [maxDrop, setMaxDrop] = useState("");
    const [cursor, setCursor] = useState({ x: 250, y: 620, opacity: 0 });
    const [tap, setTap] = useState(0);

    useEffect(() => {
        const onVis = () => setTabVisible(!document.hidden);
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, []);

    useEffect(() => {
        if (!inView || !tabVisible) return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        let cancelled = false;
        const wait = (ms) => new Promise((r) => setTimeout(r, ms));

        const moveTo = async (selector) => {
            const box = screenRef.current;
            const el = box?.querySelector(selector);
            if (!box || !el || cancelled) return;
            const c = box.getBoundingClientRect();
            const r = el.getBoundingClientRect();
            setCursor({
                x: r.left - c.left + r.width / 2 - 2,
                y: r.top - c.top + r.height / 2 - 2,
                opacity: 1,
            });
            await wait(850);
            if (cancelled) return;
            setTap((t) => t + 1);
            el.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
            el.click();
            await wait(220);
        };

        const typeText = async (setter, text) => {
            for (let i = 1; i <= text.length; i++) {
                if (cancelled) return;
                setter(text.slice(0, i));
                await wait(150);
            }
        };

        const run = async () => {
            while (!cancelled) {
                setScreen(0);
                setPhase("setup");
                setPill("All");
                setPickup("");
                setMinDrop("");
                setMaxDrop("");
                setCursor({ x: 250, y: 620, opacity: 0 });
                await wait(1200);
                if (cancelled) return;

                await moveTo('[data-testid="pill-parcel"]');
                if (cancelled) return;
                await wait(450);
                await moveTo('[data-testid="demo-continue-btn"]');
                setScreen(1);
                await wait(750);
                if (cancelled) return;

                await moveTo('[data-testid="field-pickup"]');
                await typeText(setPickup, "2.0");
                await wait(250);
                await moveTo('[data-testid="demo-continue-btn"]');
                setScreen(2);
                await wait(750);
                if (cancelled) return;

                await moveTo('[data-testid="field-min"]');
                await typeText(setMinDrop, "30.0");
                await moveTo('[data-testid="field-max"]');
                await typeText(setMaxDrop, "100.0");
                await moveTo('[data-testid="demo-continue-btn"]');
                setScreen(3);
                await wait(850);
                if (cancelled) return;

                await moveTo('[data-testid="start-automation-btn"]');
                setPhase("starting");
                await wait(1200);
                if (cancelled) return;
                setPhase("order");
                await wait(1300);
                if (cancelled) return;
                setPhase("cursor");
                await moveTo('[data-testid="accept-ride-btn"]');
                setPhase("accepted");
                await wait(600);
                setPhase("done");
                await wait(2200);
                if (cancelled) return;
                setPhase("clear");
                setCursor((c) => ({ ...c, opacity: 0.35 }));
                await wait(800);
                if (cancelled) return;
                setPhase("order2");
                await wait(1400);
                if (cancelled) return;
                await moveTo('[data-testid="decline-ride-btn"]');
                if (cancelled) return;
                setPhase("rejected");
                await wait(1400);
                if (cancelled) return;
                setPhase("skip");
                await wait(2000);
                if (cancelled) return;
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [inView, tabVisible]);

    const showOverlay = overlayPhases.includes(phase);
    const accepted = phase === "accepted" || phase === "done";
    const secondOrder = phase === "order2" || phase === "rejected";
    const rejected = phase === "rejected";
    const skipped = phase === "skip";
    const order = secondOrder ? ORDER_TWO : ORDER_ONE;
    const next = () => setScreen((s) => Math.min(3, s + 1));
    const back = () => setScreen((s) => Math.max(0, s - 1));
    const startAutomation = () => setPhase((p) => (p === "setup" ? "starting" : p));

    return (
        <div ref={ref} className="relative flex items-center gap-6" data-testid="phone-demo">
            <div className="hidden flex-col gap-3 xl:flex">
                {guides.map((g, i) => (
                    <div
                        key={g}
                        data-testid={`guide-step-${i + 1}`}
                        className={`flex items-center gap-3 rounded-2xl border px-4 py-3 transition-all duration-500 ${
                            screen === i
                                ? "border-brand/40 bg-white opacity-100 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.12)]"
                                : "border-line bg-white/60 opacity-40"
                        }`}
                    >
                        <span className={`font-mono text-xs ${screen === i ? "text-brand" : "text-mute"}`}>
                            0{i + 1}
                        </span>
                        <span className="whitespace-nowrap text-xs font-bold text-ink">{g}</span>
                    </div>
                ))}
            </div>

            <div className="relative z-10 h-[540px] w-[270px] rounded-[3rem] border border-line bg-white p-2 shadow-[0_48px_90px_-30px_rgba(17,17,16,0.28)] sm:h-[600px] sm:w-[310px]">
                <div
                    ref={screenRef}
                    className="relative h-full w-full overflow-hidden rounded-[2.5rem] bg-[#1F1F3D]"
                >
                    <div className="absolute left-1/2 top-2.5 z-40 h-5 w-24 -translate-x-1/2 rounded-full bg-ink" />
                    <div className="absolute left-0 right-0 top-0 z-30 flex items-center justify-between px-4 pt-1.5 text-[9px] font-semibold text-white/90">
                        <span>01:30</span>
                        <span className="flex items-center gap-1">
                            <Signal size={9} />
                            <Wifi size={9} />
                            <BatteryMedium size={12} />
                        </span>
                    </div>

                    <div className="absolute inset-0">
                        <AnimatePresence>
                            <motion.div
                                key={screen}
                                initial={{ opacity: 0, x: 26 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -26 }}
                                transition={{ duration: 0.3, ease }}
                                className="absolute inset-0"
                            >
                                {screen === 0 && (
                                    <ScreenOne onNext={next} pill={pill} onSelectPill={setPill} />
                                )}
                                {screen === 1 && (
                                    <ScreenTwo onNext={next} onBack={back} pickup={pickup} />
                                )}
                                {screen === 2 && (
                                    <ScreenThree
                                        onNext={next}
                                        onBack={back}
                                        minDrop={minDrop}
                                        maxDrop={maxDrop}
                                    />
                                )}
                                {screen === 3 && (
                                    <ScreenFour
                                        onBack={back}
                                        phase={phase}
                                        onStart={startAutomation}
                                    />
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    <AnimatePresence>
                        {showOverlay && (
                            <motion.div
                                key="backdrop"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="absolute inset-0 z-10 bg-[#111128]/50 backdrop-blur-[2px]"
                            />
                        )}
                        {showOverlay && (
                            <motion.div
                                key={secondOrder ? "order-2" : "order-1"}
                                data-testid="order-overlay"
                                initial={{ y: "-130%" }}
                                animate={
                                    rejected ? { y: 0, x: [0, -6, 6, -3, 3, 0] } : { y: 0, x: 0 }
                                }
                                exit={{ y: "-130%", opacity: 0 }}
                                transition={
                                    rejected
                                        ? { duration: 0.45 }
                                        : { type: "spring", stiffness: 210, damping: 23 }
                                }
                                className={`absolute left-2.5 right-2.5 top-9 z-20 rounded-2xl bg-white p-3.5 shadow-[0_24px_50px_-12px_rgba(0,0,0,0.45)] ${
                                    rejected ? "ring-2 ring-[#EF4444]" : ""
                                }`}
                            >
                                <div className="mb-1.5 flex items-center justify-between">
                                    <span className="flex items-center gap-1.5">
                                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#1E1E30]">
                                            <Package size={10} className="text-white" />
                                        </span>
                                        <span className="text-[10px] font-bold text-[#1E1E30]">
                                            Parcel
                                        </span>
                                    </span>
                                    <ShoppingBag size={18} className="text-[#5B5BD6]" />
                                </div>
                                <p className="font-display text-xl font-bold text-[#1E1E30]">
                                    {order.price}{" "}
                                    {order.extra && (
                                        <span className="text-sm font-bold text-[#16A34A]">
                                            + {order.extra}
                                        </span>
                                    )}
                                </p>
                                <div className="my-2.5">
                                    <div className="flex gap-2">
                                        <div className="flex w-2 flex-col items-center pt-1">
                                            <span className="h-1.5 w-1.5 rounded-full bg-[#1E1E30]" />
                                            <span className="w-px flex-1 bg-[#D8D8E0]" />
                                        </div>
                                        <div className="pb-1.5">
                                            <p className="text-[11px] font-bold text-[#1E1E30]">
                                                {order.pickupKm}
                                            </p>
                                            <p className="text-[8.5px] leading-snug text-[#8A8A99]">
                                                {order.pickupAddr}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <div className="flex w-2 justify-center pt-1">
                                            <span className="text-[10px] font-bold leading-none text-[#1E1E30]">
                                                ▾
                                            </span>
                                        </div>
                                        <div>
                                            <p className="text-[11px] font-bold text-[#1E1E30]">
                                                {order.dropKm}
                                            </p>
                                            <p className="text-[8.5px] leading-snug text-[#8A8A99]">
                                                {order.dropAddr}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                {accepted ? (
                                    <motion.div
                                        data-testid="accept-ride-btn"
                                        animate={{ scale: [1, 0.94, 1] }}
                                        className="flex w-full items-center justify-center gap-2 rounded-full bg-[#16A34A] py-3 text-xs font-bold text-white"
                                    >
                                        <Check size={13} /> Accepted
                                    </motion.div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <span
                                            data-testid="decline-ride-btn"
                                            className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E4E4EA] bg-white"
                                        >
                                            <svg
                                                className="absolute inset-0 h-10 w-10 -rotate-90"
                                                viewBox="0 0 40 40"
                                            >
                                                <motion.circle
                                                    cx="20"
                                                    cy="20"
                                                    r="17"
                                                    fill="none"
                                                    stroke="#C9C9D4"
                                                    strokeWidth="2.5"
                                                    strokeDasharray="106.8"
                                                    initial={{ strokeDashoffset: 0 }}
                                                    animate={{ strokeDashoffset: 106.8 }}
                                                    transition={{
                                                        duration: 6,
                                                        repeat: Infinity,
                                                        ease: "linear",
                                                    }}
                                                />
                                            </svg>
                                            <Minus size={13} className="text-[#1E1E30]" />
                                        </span>
                                        <button
                                            data-testid="accept-ride-btn"
                                            className="flex-1 rounded-full bg-[#F5B301] py-3 text-xs font-bold text-[#1E1E30]"
                                        >
                                            Accept
                                        </button>
                                    </div>
                                )}
                                {rejected && (
                                    <motion.p
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="mt-2 rounded-lg bg-[#FEF2F2] py-1.5 text-center text-[9px] font-bold text-[#EF4444]"
                                    >
                                        Skipped — pickup too far for your rules
                                    </motion.p>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <AnimatePresence>
                        {accepted && (
                            <motion.div
                                key="toast"
                                data-testid="success-toast"
                                initial={{ y: 24, opacity: 0, x: "-50%" }}
                                animate={{ y: 0, opacity: 1, x: "-50%" }}
                                exit={{ opacity: 0, x: "-50%" }}
                                transition={{ duration: 0.35, ease }}
                                className="absolute bottom-4 left-1/2 z-40 flex items-center gap-1.5 whitespace-nowrap rounded-full bg-ink px-3.5 py-2 text-[10px] font-bold text-white shadow-lg"
                            >
                                <Check size={11} className="text-brand" /> Auto-accepted in 32ms
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <AnimatePresence>
                        {skipped && (
                            <motion.div
                                key="skip-toast"
                                data-testid="skip-toast"
                                initial={{ y: 24, opacity: 0, x: "-50%" }}
                                animate={{ y: 0, opacity: 1, x: "-50%" }}
                                exit={{ opacity: 0, x: "-50%" }}
                                transition={{ duration: 0.35, ease }}
                                className="absolute bottom-4 left-1/2 z-40 flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[#EF4444] px-3.5 py-2 text-[10px] font-bold text-white shadow-lg"
                            >
                                <X size={11} /> Skipped — pickup too far
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <motion.div
                        data-testid="demo-cursor"
                        className="pointer-events-none absolute left-0 top-0 z-50"
                        animate={{ x: cursor.x, y: cursor.y, opacity: cursor.opacity }}
                        transition={{
                            type: "spring",
                            stiffness: 170,
                            damping: 22,
                            opacity: { duration: 0.3 },
                        }}
                    >
                        <MousePointer2
                            size={22}
                            className="fill-white text-[#1E1E30] drop-shadow-[0_3px_6px_rgba(0,0,0,0.4)]"
                        />
                    </motion.div>
                    <AnimatePresence>
                        {tap > 0 && (
                            <motion.span
                                key={tap}
                                className="pointer-events-none absolute z-50 rounded-full border-2 border-white"
                                style={{ left: cursor.x, top: cursor.y }}
                                initial={{ width: 6, height: 6, x: -3, y: -3, opacity: 0.9 }}
                                animate={{ width: 34, height: 34, x: -17, y: -17, opacity: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5, ease: "easeOut" }}
                            />
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
}
